import { Button } from '@/components/ui/button';
import {
  useConversationCalls,
  useLaunchOutboundCall,
  useVoiceReceptionist,
} from '@/features/voice-receptionist/api';
import { useOpenCallerSms } from '@/features/voice-receptionist/api/use-open-caller-sms';
import { useResolvedRoutes } from '@/lib/use-routes';
import { cn } from '@/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import {
  LoaderCircleIcon,
  MessageSquareIcon,
  PhoneIcon,
  SparklesIcon,
} from 'lucide-react';

/** The clinic's voice switch gates both call actions; messaging stays independent. */
export function PhoneThreadActions({
  conversationId,
  leadId,
  compact = false,
  messageOnly = false,
  className,
}: {
  conversationId: string;
  leadId?: string | null;
  compact?: boolean;
  messageOnly?: boolean;
  className?: string;
}) {
  const routes = useResolvedRoutes();
  const navigate = useNavigate();
  const { openSms, isOpening } = useOpenCallerSms(conversationId, (id) => {
    if (routes) void navigate({ to: routes.clientsInbox, search: { id } });
  });
  const { calls, callerPhone, smsConversationId, outboundEligibleLeadId } =
    useConversationCalls(conversationId, true);
  const { query: voiceSettings } = useVoiceReceptionist();
  const { launchCall, isStarting, startedCallSid } =
    useLaunchOutboundCall(conversationId);
  const startedCall = calls.find((call) => call.id === startedCallSid);
  const wasStarted = Boolean(
    startedCallSid &&
      !startedCallSid.startsWith('CAdryrun') &&
      (!startedCall || startedCall.status === 'in_progress')
  );
  const activeCall =
    isStarting ||
    wasStarted ||
    calls.some(
      (call) => call.status === 'in_progress' && !call.id.startsWith('CAdryrun')
    );
  const canCall = Boolean(
    !voiceSettings.isError &&
      callerPhone &&
      voiceSettings.data?.visible &&
      voiceSettings.data.enabled &&
      voiceSettings.data.outboundAvailable &&
      !messageOnly
  );
  const showMessage =
    !compact && Boolean(routes && (callerPhone || smsConversationId));
  if (!canCall && !showMessage) return null;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1',
        !compact && 'justify-center',
        className
      )}
      role="group"
      aria-label={
        messageOnly ? 'Messaging actions' : 'Call and message actions'
      }
    >
      {canCall ? (
        <Button
          asChild={!activeCall}
          size="sm"
          variant="ghost"
          className="h-11 rounded-lg px-3 text-xs shadow-none sm:h-9"
          disabled={activeCall}
          title={
            activeCall
              ? 'A call with this customer is in progress'
              : 'Call from your phone or calling app'
          }
        >
          {activeCall ? (
            <>
              <PhoneIcon className="size-4" aria-hidden />
              Call back
            </>
          ) : (
            <a href={`tel:${callerPhone}`}>
              <PhoneIcon className="size-4" aria-hidden />
              Call back
            </a>
          )}
        </Button>
      ) : null}
      {showMessage ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-11 rounded-lg px-3 text-xs shadow-none sm:h-9"
          disabled={isOpening}
          onClick={() => {
            if (smsConversationId && routes)
              void navigate({
                to: routes.clientsInbox,
                search: { id: smsConversationId },
              });
            else openSms();
          }}
        >
          <MessageSquareIcon className="size-4" aria-hidden />
          {isOpening ? 'Opening…' : 'Message'}
        </Button>
      ) : null}
      {canCall && leadId && outboundEligibleLeadId === leadId ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-11 rounded-lg px-3 text-xs shadow-none sm:h-9"
          disabled={isStarting || activeCall}
          onClick={() => launchCall(leadId)}
        >
          {isStarting ? (
            <LoaderCircleIcon
              className="size-3.5 motion-safe:animate-spin"
              aria-hidden
            />
          ) : (
            <SparklesIcon className="size-3.5" aria-hidden />
          )}
          {isStarting
            ? 'Starting call…'
            : activeCall
              ? 'AI call in progress'
              : 'Ask AI to call'}
        </Button>
      ) : null}
    </div>
  );
}
